import { gantt, Link } from 'dhtmlx-gantt/dhtmlxgantt';
import { min } from 'date-fns';
import { transformUtcToZonedTime } from 'utils/helpers';
import { TEMP_ITEM_ID_COEFFICIENT } from 'utils/constants';
import { IPhase, IPhaseItem, IPhaseLinkedItem, PredecessorsTypesEnum } from 'redux-store/types';
import { GanttLinkTypesEnum, PhaseStatuses } from 'utils/types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { LagTypeEnum } from 'redux-store/api/company';
import { rowElementColorHelper } from 'utils/helpers/scheduleHelpsers';
import { calculateBusinessDays, postDateHelper } from 'utils/postDateHelper';
import { v4 as uuidv4 } from 'uuid';
import { IProject } from 'redux-store/api/projects';
import { RefObject } from 'react';
dayjs.extend(utc);

type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const getStartProjectDate = (itemsList: IPhaseItem[], projectTz?: string) => {
  const datesArray: Date[] = [];

  itemsList.forEach((item) => {
    if (item?.startDate) {
      datesArray.push(transformUtcToZonedTime(item.startDate, projectTz));
    }
  });

  if (datesArray.length > 0) {
    return min(datesArray);
  }

  return transformUtcToZonedTime(new Date().toISOString(), projectTz);
};

const normilizeLinkItem = (predecessor: any) => {
  return {
    id: uuidv4(),
    source: Number(predecessor?.predecessorId) * TEMP_ITEM_ID_COEFFICIENT,
    target: Number(predecessor?.itemId) * TEMP_ITEM_ID_COEFFICIENT,
    lag: predecessor?.lagDays || 0,
    type: `${GanttLinkTypesEnum[PredecessorsTypesEnum.FINISH_TO_START]}`,
  };
};

export const handleGanttLink = (
  actualProject: IProject,
  programmaticLinkRef: RefObject<{ source: number; target: number } | null>
) => {
  const allPredecessors = actualProject.phases.flatMap(({ items = [] }) =>
    items.flatMap(({ predecessors = [] }) => predecessors)
  );

  const normalizedAllPredecessors = allPredecessors.map(normilizeLinkItem);

  const allExistingPredecessors = gantt.getLinks();

  const makeKey = (link: Link) => `${link.source}-${link.target}-${link.type}-${link.lag}`;

  const newLinksMap = new Map(normalizedAllPredecessors.map((l) => [makeKey(l), l]));
  const existingLinksMap = new Map(allExistingPredecessors.map((l) => [makeKey(l), l]));

  const toAdd = normalizedAllPredecessors.filter((l) => !existingLinksMap.has(makeKey(l)));
  const toRemove = allExistingPredecessors.filter((l) => !newLinksMap.has(makeKey(l)));

  if (!programmaticLinkRef.current && !!toAdd.length) {
    const { source, target } = toAdd.at(-1);
    programmaticLinkRef.current = { source, target };
  }

  if (toRemove.length) {
    for (const link of toRemove) {
      gantt.deleteLink(link.id);
    }
  }

  if (toAdd.length) {
    for (const link of toAdd) {
      gantt.addLink(link);
    }
  }
};

export const addGanttItem = (item: IPhaseItem<IPhaseLinkedItem>, workingDays: number[]) => {
  const itemStartDate = item?.startDate
    ? new Date(new Date(item?.startDate).toLocaleString('en-US', { timeZone: 'Greenwich' }))
    : undefined;

  const itemEndDate = item.endDate
    ? new Date(new Date(item.endDate).toLocaleString('en-US', { timeZone: 'Greenwich' }))
    : undefined;

  itemStartDate.setHours(0, 0, 0);
  itemEndDate.setHours(0, 0, 0);

  const duration = calculateBusinessDays(
    itemStartDate.toDateString(),
    itemEndDate.toDateString(),
    workingDays
  );

  gantt.createTask({
    id: item.id * TEMP_ITEM_ID_COEFFICIENT,
    text: item.title,
    exel_text: item.title,
    start_date: itemStartDate,
    end_date: itemEndDate,
    parent: item.phaseId,
    constraint_type: 'snet',
    constraint_date: itemStartDate,
    item_status: item?.status,
    ownStartDate: item?.startDate,
    ownEndDate: item?.endDate,
    ownPredecessors: item?.predecessors,
    ownLinkedItems: item?.linkedItems,
    checklist: item?.checklist,
    exel_duration: `${duration} `,
    loading: false,
  });
};

export const getInitialData = (
  phases: IPhase[],
  workingDays: number[],
  phasesCollapsed: Record<number, boolean>,
  hidePhases?: boolean
) => {
  if (phases) {
    const formattedPhasesList = phases?.map((phase) => {
      const duration = calculateBusinessDays(phase?.startDate, phase?.endDate, workingDays);
      const open = hidePhases ? true : !phasesCollapsed[phase.id];

      return {
        id: phase.id,
        text: phase.title,
        exel_text: phase.title,
        open,
        type: gantt.config.types.task,
        color: phase.color,
        end_date: phase?.endDate
          ? new Date(new Date(phase?.endDate).toLocaleString('en-US', { timeZone: 'Greenwich' }))
          : undefined,
        start_date: phase?.startDate
          ? new Date(new Date(phase?.startDate).toLocaleString('en-US', { timeZone: 'Greenwich' }))
          : undefined,
        item_status: phase?.status,
        ownStartDate: phase?.startDate,
        ownEndDate: phase?.endDate,
        exel_duration: `${duration} `,
        loading: false,
      };
    });

    const itemsList = phases
      ?.flatMap(({ items }) => items)
      ?.sort((a, b) => Date.parse(a?.startDate) - Date.parse(b?.startDate));

    const formattedItemsList = itemsList?.map((item) => {
      const itemStartDate = item?.startDate
        ? new Date(new Date(item?.startDate).toLocaleString('en-US', { timeZone: 'Greenwich' }))
        : undefined;

      const itemEndDate = item.endDate
        ? new Date(new Date(item.endDate).toLocaleString('en-US', { timeZone: 'Greenwich' }))
        : undefined;

      itemStartDate.setHours(0, 0, 0);
      itemEndDate.setHours(0, 0, 0);

      const duration = calculateBusinessDays(
        itemStartDate.toDateString(),
        itemEndDate.toDateString(),
        workingDays
      );

      return {
        id: item.id * TEMP_ITEM_ID_COEFFICIENT,
        text: item.title,
        exel_text: item.title,
        start_date: itemStartDate,
        end_date: itemEndDate,
        parent: item.phaseId,
        constraint_type: 'snet',
        constraint_date: itemStartDate,
        item_status: item?.status,
        ownStartDate: item?.startDate,
        ownEndDate: item?.endDate,
        ownPredecessors: item?.predecessors,
        ownLinkedItems: item?.linkedItems,
        checklist: item?.checklist,
        exel_duration: `${duration} `,
        loading: false,
      };
    });

    const links = itemsList
      .filter(({ startDate, endDate }) => startDate && endDate)
      .flatMap(({ predecessors }) => predecessors)
      .filter((item) => {
        const currentPredecessor = itemsList.find(({ id }) => id === item.predecessorId);

        return (
          !!currentPredecessor && !!currentPredecessor.startDate && !!currentPredecessor.endDate
        );
      })
      .map((predecessor) => {
        return {
          id: uuidv4(),
          source: predecessor?.predecessorId * TEMP_ITEM_ID_COEFFICIENT,
          target: predecessor?.itemId * TEMP_ITEM_ID_COEFFICIENT,
          type: `${GanttLinkTypesEnum[predecessor.taskDependency]}`,
          lag: predecessor?.lagDays || 0,
        };
      });

    return {
      data: [...formattedItemsList, ...formattedPhasesList],
      links,
    };
  }

  return {
    data: [],
  };
};

export const handleSetError = (
  err: any,
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>
) => {
  if (typeof err.data?.message === 'string') {
    if (err.data.error === 'Not Found') {
      return null;
    } else {
      if (err.data.message === 'Item with this (phaseId, title) pair has already existed') {
        setErrorMessage('Item with this title has already existed');
      } else if (
        err.data.message === 'Project phase with this (projectId, title) pair has already existed'
      ) {
        setErrorMessage('Project phase with this title has already existed');
      } else {
        setErrorMessage(err.data?.message);
      }
    }
  } else {
    setErrorMessage('Something went wrong, please try again');
  }
};

export const rowElement = (
  color: string,
  text: string,
  item: boolean,
  duration: number,
  completed: string,
  loading?: boolean
) => {
  const length = text.length;
  let allow = true;

  if (length > duration * 8) {
    allow = false;
  }

  const isCompleted = completed === PhaseStatuses.completed;

  const colors = rowElementColorHelper(color);

  const getRowColor = () => {
    if (isCompleted) {
      return {
        color: '#BCBDC3',
        resize: '#BCBDC3',
        text: 'white',
        light: '#4d6ef5',
        itemText: 'white',
        phaseText: 'white',
        resizeButton: '#BCBDC3',
      };
    }

    return colors;
  };

  const getCompleteIcon = (
    style?: string
  ) => `<svg width="18" height="18" style='${style}' viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5.5026 0.895508C2.6851 0.895508 0.398438 3.18217 0.398438 5.99967C0.398438 8.81717 2.6851 11.1038 5.5026 11.1038C8.3201 11.1038 10.6068 8.81717 10.6068 5.99967C10.6068 3.18217 8.3201 0.895508 5.5026 0.895508ZM7.55958 5.08093L5.17593 7.45946C5.10447 7.53602 5.00749 7.57177 4.90541 7.57177C4.80843 7.57177 4.71145 7.53602 4.63489 7.45946L3.44563 6.27021C3.2976 6.12219 3.2976 5.87716 3.44563 5.72914C3.59365 5.58112 3.83865 5.58112 3.98667 5.72914L4.90541 6.64791L7.01854 4.53989C7.16656 4.38677 7.41156 4.38677 7.55958 4.53989C7.7076 4.68791 7.7076 4.92781 7.55958 5.08093Z"
        fill="#F9F9F9"
      />
    </svg>`;

  const getItemLoader = (style?: string) =>
    `<svg width="16" height="16" fill="currentColor" style='${style}' viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><style>.spinner_OSmW{transform-origin:center;animation:spinner_T6mA .75s step-end infinite}@keyframes spinner_T6mA{8.3%{transform:rotate(30deg)}16.6%{transform:rotate(60deg)}25%{transform:rotate(90deg)}33.3%{transform:rotate(120deg)}41.6%{transform:rotate(150deg)}50%{transform:rotate(180deg)}58.3%{transform:rotate(210deg)}66.6%{transform:rotate(240deg)}75%{transform:rotate(270deg)}83.3%{transform:rotate(300deg)}91.6%{transform:rotate(330deg)}100%{transform:rotate(360deg)}}</style><g class="spinner_OSmW"><rect x="11" y="1" width="2" height="5" opacity=".14"/><rect x="11" y="1" width="2" height="5" transform="rotate(30 12 12)" opacity=".29"/><rect x="11" y="1" width="2" height="5" transform="rotate(60 12 12)" opacity=".43"/><rect x="11" y="1" width="2" height="5" transform="rotate(90 12 12)" opacity=".57"/><rect x="11" y="1" width="2" height="5" transform="rotate(120 12 12)" opacity=".71"/><rect x="11" y="1" width="2" height="5" transform="rotate(150 12 12)" opacity=".86"/><rect x="11" y="1" width="2" height="5" transform="rotate(180 12 12)"/></g></svg>`;

  const itemDiv = `
  <div 
      style='
        display: flex;
        justify-content: center;
        align-items: center;
        background: ${getRowColor().color};
        border-radius: 4px;
        width: 100%;
        position: relative;
        overflow: hidden;
        top: -1px'
      class='hover-item-container'
  > 
    <div
      id='task-container'
      style='
        width: 100%;
        z-index: 2;
        display: flex;
        justify-content: space-between;
        align-items: center;'
    >
      <div 
        class='hover-item'
        style="
          width: 10px;
          height: 30px;
          background: ${getRowColor().resize};
          display: flex;
          justify-content: center;
          align-items: center;
          position: absolute;
          left: 0;"
      >
        <div style="
          width: 1px;
          height: 22px;
          background: ${getRowColor().resizeButton};
          border-radius: 10px;
        ">
        </div>
      </div>

      <div></div>

      <div
        id='task-element'
        class='text-container'
        style='
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 0 10px;
          overflow: hidden;
          gap: 4px;
        '>
        ${isCompleted ? getCompleteIcon(`opacity: ${allow ? 1 : 0}`) : ''}
        <span style='
          color: ${getRowColor().phaseText}; 
          text-overflow: ellipsis;
          overflow: hidden;
          opacity: ${!allow && (loading || isCompleted) ? 0 : 1}
        '>${text}
        </span>
        ${loading ? getItemLoader(`opacity: ${allow ? 1 : 0}`) : ''}
      </div>

      ${
        !allow && loading
          ? `<div style='position: absolute; left: calc(50% - 8px); top: 4px; height: 20px'>${getItemLoader()}</div>`
          : ''
      }

      ${
        !allow && isCompleted
          ? `<div style='position: absolute; left: calc(50% - 9.5px); top: 4.5px; height: 20px'>${getCompleteIcon()}</div>`
          : ''
      }

      <div></div>

      <div 
        class='hover-item'
        style="
          width: 10px;
          height: 30px;
          background: ${getRowColor().resize};
          display: flex;
          justify-content: center;
          align-items: center;
          position: absolute;
          right: 0;"
      >
        <div style="
          width: 1px;
          height: 22px;
          background: ${getRowColor().resizeButton};
          border-radius: 10px;
        ">
        </div>
      </div>
    </div> 
  </div>`;

  if (item) {
    return itemDiv;
  }

  return `<div class='tasks-parent' style='background: ${
    getRowColor().color
  }; width: 100%; border-radius: 4px; color: ${getRowColor().phaseText}; overflow: hidden; text-overflow: ellipsis; padding: 0 12px;'>${text}</div>`;
};

export function calculateStartToStartEndDate(
  startDateBefore: Date | string,
  startDateAfter: Date | string,
  duration: number | string,
  workingDays: Weekday[]
): Date {
  const totalDays = typeof duration === 'string' ? parseInt(duration, 10) : duration;

  if (isNaN(totalDays) || totalDays < 0) {
    throw new Error(`Invalid duration: ${duration}`);
  }

  const before = dayjs.utc(startDateBefore).startOf('day');
  const after = dayjs.utc(startDateAfter).startOf('day');

  const cursor = after;

  let addedDays = 0;
  let addedWorkingDays = 0;
  let dow = cursor.day() as Weekday;

  while (addedWorkingDays < totalDays) {
    addedDays++;
    dow = ((dow + 1) % 7) as Weekday;
    if (workingDays.includes(dow)) {
      addedWorkingDays++;
    }
  }

  let end = cursor.add(addedDays, 'day');
  while (!workingDays.includes(end.day() as Weekday)) {
    end = end.add(1, 'day');
  }

  return end.startOf('day').toDate();
}

// linkd item start date calculation

function isWorkingDay(weekday, workingDaysArray) {
  return workingDaysArray?.includes(weekday);
}

export function addWorkingDays(
  firstDate: Date | string,
  workingDaysArray: number[],
  lagDays: number | string
) {
  const oneDay = 24 * 60 * 60 * 1000;
  let currentDate = new Date(firstDate);
  let workingDaysAdded = 0;

  currentDate.setHours(0, 0, 0, 0);

  while (workingDaysAdded < Number(lagDays)) {
    currentDate.setTime(currentDate.getTime() + oneDay);
    const currentDay = currentDate.getDay();

    if (isWorkingDay(currentDay, workingDaysArray)) {
      workingDaysAdded += 1;
    }
  }

  currentDate.setHours(0, 0, 0, 0);

  return currentDate;
}

function subtractWorkingDays(endDate, workingDaysArray, numWorkingDays) {
  const oneDay = 24 * 60 * 60 * 1000;
  let currentDate = new Date(endDate);
  let workingDaysSubtracted = 0;

  currentDate.setHours(0, 0, 0, 0);

  while (workingDaysSubtracted < numWorkingDays) {
    currentDate.setTime(currentDate.getTime() - oneDay);
    const currentDay = currentDate.getDay();

    if (isWorkingDay(currentDay, workingDaysArray)) {
      workingDaysSubtracted += 1;
    }
  }

  currentDate.setHours(0, 0, 0, 0);

  return currentDate;
}

function calculateItemStartDate(endDate, duration, workingDaysArray) {
  const itemStartDate = subtractWorkingDays(endDate, workingDaysArray, duration);

  const startDay = new Date(itemStartDate).getDay();

  if (!isWorkingDay(startDay, workingDaysArray)) {
    const daysToAdjust = startDay === 0 ? 2 : 1;
    return subtractWorkingDays(itemStartDate, workingDaysArray, daysToAdjust);
  }

  return itemStartDate;
}

export const calculateLinkedItemStartDate = (
  firstItemStartDate: Date | string,
  firstItemEndDate: Date | string,
  lagDays: number | string,
  workingDaysArray: number[],
  type: string | number,
  duration: string | number,
  endDate?: Date | string
) => {
  switch (type) {
    case 'FINISH-TO-START':
      const F2SEndDate = new Date(
        new Date(firstItemEndDate).setDate(new Date(firstItemEndDate).getDate() - 1)
      );

      F2SEndDate.setHours(0, 0, 0, 0);

      return addWorkingDays(F2SEndDate, workingDaysArray, Number(lagDays) + 1);

    case 'START-TO-START':
      return addWorkingDays(firstItemStartDate, workingDaysArray, lagDays);

    case 'FINISH-TO-FINISH':
      return calculateItemStartDate(endDate, duration, workingDaysArray);

    case 'START-TO-FINISH':
      return calculateItemStartDate(endDate, duration, workingDaysArray);

    default:
      return addWorkingDays(firstItemStartDate, workingDaysArray, lagDays);
  }
};

// linked item end date calculation

export const startToStartEndDate = (
  newStartDate: Date | string,
  duration: number | string,
  workingDays: number[]
): Date => {
  const start = dayjs(newStartDate).startOf('day');

  const totalDays = typeof duration === 'string' ? parseInt(duration, 10) : duration;

  if (isNaN(totalDays) || totalDays < 0) {
    throw new Error(`Invalid duration: ${duration}`);
  }

  let addedDays = 0;
  let addedWorkingDays = 0;
  let dow = start.day() as Weekday;

  while (addedWorkingDays < totalDays) {
    addedDays++;
    dow = ((dow + 1) % 7) as Weekday;
    if (workingDays.includes(dow)) {
      addedWorkingDays++;
    }
  }

  const end = start.add(addedDays, 'day').startOf('day');

  return end.toDate();
};

export const calculateLinkedItemEndDate = (
  firstItemStartDate: Date | string,
  lagDays: number | string,
  workingDaysArray: number[],
  currentStartDate: Date | string,
  newStartDate: Date | string,
  duration: string | number,
  workingDays: number[],
  type: string | number,
  firstItemEndDate: Date | string
) => {
  switch (type) {
    case 'FINISH-TO-START':
      return startToStartEndDate(newStartDate, duration, workingDays);

    case 'START-TO-START':
      return startToStartEndDate(newStartDate, duration, workingDays);

    case 'FINISH-TO-FINISH':
      const F2FEndDate = new Date(
        new Date(firstItemEndDate).setDate(new Date(firstItemEndDate).getDate() - 1)
      );

      F2FEndDate.setHours(0, 0, 0, 0);

      return addWorkingDays(F2FEndDate, workingDaysArray, lagDays);

    case 'START-TO-FINISH':
      return addWorkingDays(firstItemStartDate, workingDaysArray, lagDays);

    default:
      return startToStartEndDate(newStartDate, duration, workingDays);
  }
};

// return predecessors dates

export const predecessorsDates = (
  updatingTaskStartDate,
  updatingTaskEndDate,
  taskItemLagDays,
  workingDays,
  duration,
  taskDependency,
  sourceTaskStartDate
) => {
  let startDate;
  let endDate;

  switch (taskDependency) {
    case 'FINISH-TO-START':
      startDate = calculateLinkedItemStartDate(
        updatingTaskStartDate,
        updatingTaskEndDate,
        taskItemLagDays,
        workingDays,
        taskDependency,
        duration
      );

      endDate = calculateLinkedItemEndDate(
        updatingTaskStartDate,
        taskItemLagDays,
        workingDays,
        sourceTaskStartDate,
        startDate,
        duration,
        workingDays,
        taskDependency,
        updatingTaskEndDate
      );

      const F2SStartDate = new Date(new Date(startDate).setDate(new Date(startDate).getDate() + 1));

      F2SStartDate.setHours(0, 0, 0, 0);

      return { startDate: startDate, endDate: endDate };

    case 'START-TO-START':
      startDate = calculateLinkedItemStartDate(
        updatingTaskStartDate,
        updatingTaskEndDate,
        taskItemLagDays,
        workingDays,
        taskDependency,
        duration
      );

      endDate = calculateLinkedItemEndDate(
        updatingTaskStartDate,
        taskItemLagDays,
        workingDays,
        sourceTaskStartDate,
        startDate,
        duration,
        workingDays,
        taskDependency,
        updatingTaskEndDate
      );

      return { startDate: startDate, endDate: endDate };

    case 'START-TO-FINISH':
      endDate = calculateLinkedItemEndDate(
        updatingTaskStartDate,
        taskItemLagDays,
        workingDays,
        sourceTaskStartDate,
        startDate,
        duration,
        workingDays,
        taskDependency,
        updatingTaskEndDate
      );

      startDate = calculateLinkedItemStartDate(
        updatingTaskStartDate,
        updatingTaskEndDate,
        taskItemLagDays,
        workingDays,
        taskDependency,
        duration,
        endDate
      );

      const newEnd = startToStartEndDate(startDate, duration, workingDays);

      return { startDate: startDate, endDate: newEnd };

    case 'FINISH-TO-FINISH':
      endDate = calculateLinkedItemEndDate(
        updatingTaskStartDate,
        taskItemLagDays,
        workingDays,
        sourceTaskStartDate,
        startDate,
        duration,
        workingDays,
        taskDependency,
        updatingTaskEndDate
      );

      const F2FendDate = new Date(new Date(endDate).setDate(new Date(endDate).getDate() + 1));

      F2FendDate.setHours(0, 0, 0, 0);

      startDate = calculateLinkedItemStartDate(
        updatingTaskStartDate,
        updatingTaskEndDate,
        taskItemLagDays,
        workingDays,
        taskDependency,
        duration,
        F2FendDate
      );

      return { startDate: startDate, endDate: F2FendDate };

    default:
      startDate = calculateLinkedItemStartDate(
        updatingTaskStartDate,
        updatingTaskEndDate,
        taskItemLagDays,
        workingDays,
        taskDependency,
        duration
      );

      endDate = calculateLinkedItemEndDate(
        updatingTaskStartDate,
        taskItemLagDays,
        workingDays,
        sourceTaskStartDate,
        startDate,
        duration,
        workingDays,
        taskDependency,
        updatingTaskEndDate
      );

      return { startDate: startDate, endDate: endDate };
  }
};

export const calculateStartDate = (
  firstItemEndDate: Date | string,
  lagDays: number | string,
  workingDaysArray: number[]
) => {
  const F2SEndDate = new Date(
    new Date(firstItemEndDate).setDate(new Date(firstItemEndDate).getDate() - 1)
  );

  F2SEndDate.setHours(0, 0, 0, 0);

  return addWorkingDays(F2SEndDate, workingDaysArray, Number(lagDays) + 1);
};

export const calculateEndDate = (
  newStartDate: Date | string,
  duration: string | number,
  workingDays: number[]
) => {
  return startToStartEndDate(newStartDate, duration, workingDays);
};

export const calculateParentItemDates = (
  predecessors: IPhaseLinkedItem[],
  scheduleItem,
  phases: IPhase[]
) => {
  const scheduleItems = phases?.flatMap(({ items }) => items);
  predecessors?.map(({ itemId, predecessorId, lagDays }) => {
    const sourceItem = scheduleItems?.find(({ id }) => id === predecessorId);
    const targetItem = scheduleItems?.find(({ id }) => id === itemId);

    const targetItemStartDate = new Date(targetItem.startDate);
    const sourceItemStartDate = new Date(
      new Date(sourceItem.startDate).setDate(new Date(sourceItem.startDate).getDate() + lagDays)
    );

    targetItemStartDate.setHours(0, 0, 0);
    sourceItemStartDate.setHours(0, 0, 0);
  });
};

export const calculateMinimumLagDays = (
  sourceItemEndDate: string | Date,
  targetItemStartDate: string | Date,
  businessDays: number,
  lagDays: number,
  workingDaysArray: number[],
  targetDuration: number,
  targetItemEndDate: string | Date,
  lagType: LagTypeEnum,
  workDaysDifference: number,
  sourceItemStartDate: string | Date,
  workingDays: number[],
  sourceDuration: number,
  actualSourceTask: IPhaseItem<IPhaseLinkedItem>,
  actualTargetTask: IPhaseItem<IPhaseLinkedItem>,
  fromModals?: boolean
) => {
  const souceEndDate = new Date(sourceItemEndDate);
  const sourceStartDate = new Date(sourceItemStartDate);
  const targetStartDate = new Date(targetItemStartDate);

  souceEndDate.setHours(0, 0, 0, 0);
  sourceStartDate.setHours(0, 0, 0, 0);
  targetStartDate.setHours(0, 0, 0, 0);

  const first = new Date(sourceItemEndDate);
  const second = new Date(targetItemStartDate);

  first.setHours(0, 0, 0);
  second.setHours(0, 0, 0);

  if (lagType === LagTypeEnum.MAINTAIN) {
    const daysDiff =
      !!workDaysDifference || workDaysDifference === 0 ? workDaysDifference : businessDays;

    if (daysDiff === 0) {
      const sourceStartDayJS = dayjs(postDateHelper(actualSourceTask.startDate, true));
      const currentSourceStartDayJS = dayjs(postDateHelper(sourceItemStartDate, true));

      const sourceEndDayJS = dayjs(postDateHelper(actualSourceTask.endDate, true)).add(1, 'day');
      const currentSourceEndDayJS = dayjs(postDateHelper(sourceItemEndDate, true));

      const currentTargetStartDayJS = dayjs(postDateHelper(targetItemStartDate, true));

      const startIsSame = sourceStartDayJS.utc().isSame(currentSourceStartDayJS.utc(), 'day');
      const endIsSame = sourceEndDayJS.utc().isSame(currentSourceEndDayJS.utc());

      if (startIsSame && !endIsSame) {
        const newEndIsAfter = currentSourceEndDayJS.utc().isAfter(sourceEndDayJS.utc());

        if (newEndIsAfter) {
          const businessDaysDiff =
            calculateBusinessDays(
              sourceEndDayJS.utc().toString(),
              currentSourceEndDayJS.utc().toString(),
              workingDays
            ) - 1;

          const startDate = calculateStartDate(targetStartDate, businessDaysDiff, workingDaysArray);
          const cEndDate = calculateEndDate(startDate, targetDuration, workingDaysArray);

          let endDate = cEndDate;

          if (fromModals) {
            endDate = new Date(new Date(cEndDate).setDate(new Date(cEndDate).getDate() - 1));
          }

          return { startDate: startDate, endDate: endDate, changed: false };
        }

        return { startDate: targetItemStartDate, endDate: targetItemEndDate, changed: false };
      }

      if (!startIsSame && endIsSame) {
        const startIsAfter = currentSourceStartDayJS.utc().isAfter(currentTargetStartDayJS.utc());

        if (startIsAfter) {
          const newEndDate = calculateEndDate(sourceStartDate, targetDuration, workingDaysArray);

          let endDate = newEndDate;

          if (fromModals) {
            endDate = new Date(new Date(newEndDate).setDate(new Date(newEndDate).getDate() - 1));
          }

          return { startDate: sourceStartDate, endDate: endDate, changed: true };
        }

        return { startDate: targetItemStartDate, endDate: targetItemEndDate, changed: false };
      }

      const businessDaysDiff =
        calculateBusinessDays(actualSourceTask.startDate, actualTargetTask.startDate, workingDays) -
        1;

      const startDate = calculateStartDate(sourceStartDate, businessDaysDiff, workingDaysArray);
      const cEndDate = calculateEndDate(startDate, targetDuration, workingDaysArray);

      let endDate = cEndDate;
      if (fromModals) {
        endDate = new Date(new Date(cEndDate).setDate(new Date(cEndDate).getDate() - 1));
      }

      return { startDate: startDate, endDate: endDate, changed: true };
    }

    const startDate = calculateStartDate(souceEndDate, daysDiff, workingDaysArray);
    const cEndDate = calculateEndDate(startDate, targetDuration, workingDaysArray);
    let endDate = cEndDate;

    if (fromModals) {
      endDate = new Date(new Date(cEndDate).setDate(new Date(cEndDate).getDate() - 1));
    }

    return { startDate: startDate, endDate: endDate, changed: true };
  }

  if (lagType === LagTypeEnum.MINIMUM) {
    if (Number(businessDays) > lagDays && first.getTime() > second.getTime() && lagDays === 0) {
      const sourceStartDayJS = dayjs(postDateHelper(actualSourceTask.startDate, true));
      const currentSourceStartDayJS = dayjs(postDateHelper(sourceItemStartDate, true));

      const sourceEndDayJS = dayjs(postDateHelper(actualSourceTask.endDate, true)).add(1, 'day');
      const currentSourceEndDayJS = dayjs(postDateHelper(sourceItemEndDate, true));

      const currentTargetStartDayJS = dayjs(postDateHelper(targetItemStartDate, true));

      const startIsSame = sourceStartDayJS.utc().isSame(currentSourceStartDayJS.utc(), 'day');
      const endIsSame = sourceEndDayJS.utc().isSame(currentSourceEndDayJS.utc());

      if (startIsSame && !endIsSame) {
        return { startDate: targetItemStartDate, endDate: targetItemEndDate, changed: false };
      }

      if (!startIsSame && endIsSame) {
        const startIsAfter = currentSourceStartDayJS.utc().isAfter(currentTargetStartDayJS.utc());

        if (startIsAfter) {
          const newEndDate = calculateEndDate(sourceStartDate, targetDuration, workingDaysArray);

          let endDate = newEndDate;

          if (fromModals) {
            endDate = new Date(new Date(newEndDate).setDate(new Date(newEndDate).getDate() - 1));
          }

          return { startDate: sourceStartDate, endDate: endDate, changed: true };
        }

        return { startDate: targetItemStartDate, endDate: targetItemEndDate, changed: false };
      }

      const newEndDate = calculateEndDate(sourceStartDate, targetDuration, workingDaysArray);

      let endDate = newEndDate;

      if (fromModals) {
        endDate = new Date(new Date(newEndDate).setDate(new Date(newEndDate).getDate() - 1));
      }

      return { startDate: sourceStartDate, endDate: endDate, changed: true };
    } else if (Number(businessDays) > lagDays && first.getTime() > second.getTime()) {
      const startDate = calculateStartDate(souceEndDate, lagDays, workingDaysArray);
      const cEndDate = calculateEndDate(startDate, targetDuration, workingDaysArray);
      let endDate = cEndDate;

      if (fromModals) {
        endDate = new Date(new Date(cEndDate).setDate(new Date(cEndDate).getDate() - 1));
      }

      return { startDate: startDate, endDate: endDate, changed: true };
    } else if (Number(businessDays) > lagDays) {
      return { startDate: targetItemStartDate, endDate: targetItemEndDate, changed: false };
    } else {
      const startDate = calculateStartDate(souceEndDate, lagDays, workingDaysArray);
      const cEndDate = calculateEndDate(startDate, targetDuration, workingDaysArray);
      let endDate = cEndDate;

      if (fromModals) {
        endDate = new Date(new Date(cEndDate).setDate(new Date(cEndDate).getDate() - 1));
      }

      return { startDate: startDate, endDate: endDate, changed: true };
    }
  }
};
